import * as React from 'react';
import {
    LoadingIndicator,
    PVATeamSelect,
    PVABadge,
    PVAPage,
    LoadingIndicatorMini,
    PVAUserMultiSelect,
    PVAStyleModal,
    PVASelect, PVAContainer,
} from '../../../components';
import { ReportType } from '../../../enums';
import { ReportDataService, StyleDataService, TeamologyDataService, Auth} from '../../../services';
import { User, UserLight, Style, Team, ReportTeamologyConfig, UserPermissions } from '../../../models';
import { BaseRouteProps } from '../../../interfaces';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { UserStore, FilterStore } from '../../../stores';
import { Roles } from '../../../stores/Constants';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PonderaModal } from '../../inclinationmodals';
import './TeamologyReport.css';

class TeamologyReportData {
    styles: Style[];
    users: UserLight[];
    generalECAverage: number;
    satisfactionAverage: number;
}

interface TeamologyReportState {
    orgId: number;
    surveyVersionId: number;
    selectedUserData: any;
    reportType: ReportType;
    reportData: TeamologyReportData | undefined;
    loading: boolean;
    teamId: number;
    fetchedTeamId: number;
    fetchedSurveyVersionId: number;
    fetchedOrgId: number;
    mode: number;
    filter: string;
    miniLoad: boolean;
    selectedUserIds: number[];
    showClarity: boolean;
    config: ReportTeamologyConfig | undefined;
    exporting: boolean;
    readyToLoadTeam: boolean;
    allStyles: Style[];
}

interface TeamologyReportProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class TeamologyReport extends React.Component<TeamologyReportProps, TeamologyReportState> {
    private reportDataService = new ReportDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);
    private teamologyDataService = new TeamologyDataService(this.props.notificationService);
    private auth = new Auth();

    constructor(props: TeamologyReportProps) {
        super(props);

        this.state = {
            orgId: 0,
            surveyVersionId: 0,
            selectedUserData: null,
            reportData: undefined,
            reportType: ReportType.Standard,
            loading: false,
            teamId: 0,
            fetchedTeamId: 0,
            fetchedSurveyVersionId: 0,
            fetchedOrgId: 0,
            mode: 0,
            filter: '',
            miniLoad: false,
            selectedUserIds: [],
            showClarity: false,
            config: undefined,
            exporting: false,
            readyToLoadTeam: false,
            allStyles: []
        };
    }

    @computed get team() {
        var team = this.teams.find((t: Team) => {
            return t.id === this.state.teamId;
        });
        if (team) {
            return team;
        }
        return null;
    }
    
    @computed get hasTeamologyPermission() {
        return this.props.UserStore!.checkUserPermission(UserPermissions.Teamology);
    }

    @computed get teamsThisUserManages() {
        const managedTeamIds = this.auth.managedTeams();
        return this.teams.filter(t => managedTeamIds.includes(t.id.toString()));
    }

    @computed get canAccessReportData() {
        return !this.props.UserStore!.checkUserRole(Roles.User) || this.teamsThisUserManages.length > 0 || this.hasTeamologyPermission;
    }

    @computed get teams() {
        return this.props.FilterStore!.teams;
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get users() {
        if (this.state.reportData) {
            return this.state.reportData.users;
        }
        return [];
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }


    @computed get teamId() {
        if (!this.state.readyToLoadTeam) {
            return 0;
        }
        return this.props.FilterStore!.teamId;
    }


    private isFetching = false;

    public update() {
        if (this.canAccessReportData) {
            if (this.organization && this.organization.id !== this.state.orgId) {
                this.setState(
                    {
                        orgId: this.organization.id,
                        teamId: 0,
                        fetchedTeamId: 0,
                        fetchedSurveyVersionId: 0,
                        fetchedOrgId: 0,
                        selectedUserIds: [],
                        selectedUserData: null,
                        readyToLoadTeam: false,
                        reportData: undefined
                    },
                    () => {
                        if (this.organization) {
                            this.teamologyDataService.getConfig(this.organization).then((settings: ReportTeamologyConfig) => {
                                this.setState({
                                    config: settings
                                });
                            });
                        }
                    });

            }

            if (this.surveyVersion && this.surveyVersion.id !== this.state.surveyVersionId) {
                this.setState({
                    surveyVersionId: this.surveyVersion.id,
                    fetchedTeamId: 0,
                    reportData: undefined
                });

                this.styleDataService.getAllBySurveyVersionId(this.surveyVersion!.id).then((styles: Style[]) => {
                    this.setState({
                        allStyles: styles,
                        reportData: {
                            styles: styles,
                            users: [],
                            generalECAverage: 0,
                            satisfactionAverage: 0
                        }
                    });
                });
            }
            
            let teamId = this.teamId;

            if (this.surveyVersion && this.survey && (this.state.fetchedTeamId !== teamId || this.state.fetchedSurveyVersionId !== this.surveyVersion.id)) {
                if (this.state.fetchedTeamId === teamId && this.state.fetchedSurveyVersionId === this.surveyVersion.id && this.state.fetchedOrgId === this.state.orgId) {
                    return;
                }

                if (!this.state.readyToLoadTeam && teamId === 0) {
                    this.setState({
                        teamId: teamId
                    });
                    return;
                }

                if (!this.state.readyToLoadTeam) {
                    return;
                }

                const surveyId = this.survey.id;
                const surveyVersionId = this.surveyVersion.id;
                const orgId = this.state.orgId;
                
                this.isFetching = true;
                this.setState({
                    miniLoad: true,
                    teamId: teamId,
                    fetchedTeamId: teamId,
                    fetchedSurveyVersionId: surveyVersionId,
                    fetchedOrgId: orgId
                }, () => {
                    var team = this.teams.find((t: Team) => {
                        return t.id === teamId;
                    });
    
                    let promise: Promise<TeamologyReportData>;
                    if (team) {
                        promise = this.reportDataService.getTeamologyTeamReportData(team, surveyId, surveyVersionId);
                    } else if (this.organization && teamId === -100) {
                        promise = this.reportDataService.getTeamologyOrganizationReportData(this.organization, surveyId, surveyVersionId);
                    } else if (this.organization && teamId === -200) {
                        promise = this.reportDataService.getTeamologyCandidateReportData(this.organization, surveyId, surveyVersionId);
                    } else if (this.organization && teamId === -300) {
                        promise = this.reportDataService.getTeamologyFormerEmployeeReportData(this.organization, surveyId, surveyVersionId);
                    } else {
                        if (this.surveyVersion) {
                            promise = this.styleDataService.getAllBySurveyVersionId(this.surveyVersion!.id).then((styles: Style[]) => {
                                return {
                                    styles: styles,
                                    users: [],
                                    generalECAverage: 0,
                                    satisfactionAverage: 0,
                                    mbtiScore: 0
                                } as any;
                            });
                        } else {                    
                            promise = Promise.resolve(undefined as any);
                        }
                    }
    
                    promise.then((reportData: TeamologyReportData) => {
                        this.isFetching = false;
                        this.setState({
                            reportData: reportData,
                            miniLoad: false
                        });
                    }).catch(() => {
                        this.isFetching = false;
                        this.setState({
                            miniLoad: false
                        });
                    });
                });
            }
        }
    }

    componentDidMount() {
        if (this.props.FilterStore) {
            this.props.FilterStore.setTeamID(0);
        }
        this.update();
    }

    componentWillReact() {
        this.update();
    }

    public submit(values: any) {
        this.reportDataService.getIndividualReportData(values.userId).then((reportData) => {
            this.setState({
                selectedUserData: reportData,
                reportType: values.reportType
            });
        });
    }

    toggleModal(name: string) {
        if (!this.state.reportData) {
            return;
        }

        this.state.allStyles.map((style: Style) => {
            if (style && style.name.toLowerCase() === name) {
                style.showModal = !style.showModal;
            }
        });
        this.forceUpdate();
    }


    userSort(a: any, b: any) {
        if (a.user.lastName > b.user.lastName) {
            return 1;
        } else if (a.user.lastName < b.user.lastName) {
            return -1;
        }
        if (a.user.firstName > b.user.firstName) {
            return 1;
        } else if (a.user.firstName < b.user.firstName) {
            return -1;
        }
        return 0;
    }

    styleMatch(user: UserLight) {
        if (!this.state.reportData) {
            return null;
        }

        var style = this.state.allStyles.find((s: any) => {
            return s.id == user.styleId;
        });
        if (style) {
            return style;
        }

        return this.state.allStyles.find((s: any) => {
            return (user.styleName == s.name && user.styleCore == s.core);
        });
    }

    teamChange(e: any) {
        if (this.props.FilterStore) {
            var id = Number(e.target.value);
            var team = this.teams.find((t: Team) => {
                return t.id === id;
            });
            if (team) {
                this.props.FilterStore.setTeam(team);
            } else {
                // -100, -200, -300, etc
                this.props.FilterStore.setTeamID(id);
            }
            // this.props.FilterStore.teamId = Number(e.target.value);
            this.setState({
                selectedUserIds: [],
                readyToLoadTeam: true,
                fetchedTeamId: 0
            });
        }
    }

    toggleUserSelect(e: any) {
        var id = Number(e.target.value);
        if (id === -1) {
            // select all users
            var tempids: number[] = [];
            this.users.forEach((u: any) => {
                if (!u.resultsArePrivate) {
                    tempids.push(u.id);
                }
            });
            this.setState({
                selectedUserIds: tempids
            });
            return;
        }
        if (id === -2) {
            // deselect all users
            this.setState({
                selectedUserIds: []
            });
            return;
        }

        var ids = [...this.state.selectedUserIds];
        var ind = ids.indexOf(id);
        if (ind > -1) {
            ids.splice(ind, 1);
        } else {
            ids.push(id);
        }
        this.setState({
            selectedUserIds: ids
        });
    }


    private getClarityClass(user: any) {
        if (user.mbtiScore < 32) {
            return 'clarity-low';
        } else if (user.mbtiScore < 61) {
            return 'clarity-mid';
        } else if (user.mbtiScore < 100) {
            return 'clarity-high';
        }
        return '';
    }

    private abbr(name: string) {
        var words = name.split(' ');
        var result = '';
        words.map((w: string) => {
            result += w[0];
        });
        return result;
    }

    private getCoreShort(style: Style, styleConnectedId: number) {
        if (this.state.reportData && style.useSecondaryStyle) {
            var subStyle = this.state.allStyles.find(x => x.id === styleConnectedId);
            if (subStyle != null) {
                return subStyle.name;
            }
        }
        return style.subCore ? this.abbr(style.subCore.name) : style.core;
    }
    private getCoreFull(style: Style, styleConnectedId: number) {
        if (this.state.reportData && style.useSecondaryStyle) {
            var subStyle = this.state.allStyles.find(x => x.id === styleConnectedId);
            if (subStyle != null) {
                return subStyle.name;
            }
        }
        return style.subCore ? style.subCore.name : style.core;
    }

    public render() {

        var styleUsers: any = {};
        var styleUserEntries: any = [];
        var styleMap: Style[] = [];
        var userCount = 0;

        if (this.state.reportData) {
            this.state.allStyles.forEach((style: Style) => {
                var styleName = style.name.toLowerCase();
                if (styleUsers[styleName] === undefined) {
                    styleUsers[styleName] = {
                        name: style.name,
                        modalVisible: style.showModal,
                        content: '',
                        tooltip: '',
                        users: []
                    };
                    if (style.styleTexts) {
                        var stBrief = style.styleTexts.find((x: any) => {
                            return x.styleTextType === 'brief';
                        });
                        var stContent = style.styleTexts.find((x: any) => {
                            return x.styleTextType === 'content';
                        });
                        if (stBrief) {
                            styleUsers[styleName].tooltip = stBrief.value;
                        }
                        if (stContent) {
                            styleUsers[styleName].content = stContent.value;
                        }
                    }
                    styleUserEntries.push(styleName);
                    styleMap.push(style);
                }

                if (this.state.reportData) {
                    this.state.reportData.users.filter((u: UserLight) => {
                        if (this.state.selectedUserIds.indexOf(u.id) === -1 && this.state.selectedUserIds.indexOf(Number(u.id)) === -1 && this.state.selectedUserIds.indexOf(u.id.toString() as any) === -1) {
                            return false;
                        }
                        if (!this.state.filter || this.state.filter === '') {
                            return true;
                        }
                        return u.firstName.toLowerCase().indexOf(this.state.filter.toLowerCase()) > -1 || u.lastName.toLowerCase().indexOf(this.state.filter.toLowerCase()) > -1;
                    }).forEach((user: UserLight) => {
                        var userStyle = this.styleMatch(user);
                        if (!!user && !!userStyle && !!user.secondaryStyleId && !!userStyle.leastPreferredStyleID) {
                            if (this.state.mode === 0 && (userStyle!.id == style.id))  {
                                styleUsers[styleName].users.push({
                                    user: user,
                                    core: this.getCoreShort(style, user.secondaryStyleId),
                                    coreFull: this.getCoreFull(style, user.secondaryStyleId)
                                });
                            } else if (this.state.mode === 1 && style.id == user.secondaryStyleId) {
                                styleUsers[styleName].users.push({
                                    user: user,
                                    core: this.getCoreShort(style, userStyle.id),
                                    coreFull: this.getCoreFull(style, userStyle.id)
                                });
                            } else if (this.state.mode === 2 && style.id == userStyle.tertiaryStyleID) {
                                styleUsers[styleName].users.push({
                                    user: user,
                                    core: this.getCoreShort(style, userStyle.id),
                                    coreFull: this.getCoreFull(style, userStyle.id)
                                });
                            } else if (this.state.mode === 3 && style.id == userStyle.leastPreferredStyleID) {
                                styleUsers[styleName].users.push({
                                    user: user,
                                    core: this.getCoreShort(style, userStyle.id),
                                    coreFull: this.getCoreFull(style, userStyle.id)
                                });
                            }
                        }
                    });
                } else {
                    console.log('no report data');
                }
            });
        }

        var resultingRows: any[] = [];
        for (var i = 0; i < styleUserEntries.length; i += 4) {
            var keys = [i + 0, i + 1, i + 2, i + 3];

            const renderPair = (ind: number) => {
                var dataName = styleUserEntries[ind];
                var style = styleMap[ind];
                var data = styleUsers[dataName];
                if (!data) {
                    return <div key={`header-${dataName}`} />;
                }
                const modalProps = {
                    show: data.modalVisible,
                    onHide: this.toggleModal.bind(this, dataName),
                    title: data.displayName ? data.displayName : data.name,
                    content: data.content,
                    style: style,
                    name: ' '
                };

                const tooltip = <Tooltip><span dangerouslySetInnerHTML={{ __html: data.tooltip }} /></Tooltip>;

                const icon = (style && style.name) ? `badges/${style.name.toLowerCase()} icon.svg` : undefined;
                
                return <div key={`header-${dataName}`} className="style-block">
                    <PVAContainer
                        id={data.name}
                        title={data.name}
                        disableAccordion={true}
                        tooltipOnTitle={true}
                        tooltipData={{
                            topic: data.name,
                            title: data.name,
                            content: data.tooltip
                        }}
                        onClickHeader={() => { this.toggleModal(dataName); }}
                    >
                        <PVAContainer.Icon>
                            {icon && <div className={`icon bg-style-${style.name.toLowerCase()}`}><img src={`images/${icon}`} alt="" /></div>}
                        </PVAContainer.Icon>
                        <div className="teamology-pva-container-content">
                            <div className="teamology-user-list">
                                {
                                    data.users.sort(this.userSort).map((d: any) => {
                                        userCount++;
                                        return <div className={"clarity " + this.getClarityClass(d.user)}>
                                            <span>{d.user.lastName}, {d.user.firstName} ({d.core})</span>
                                        </div>
                                    })
                                }
                            </div>
                        </div>
                        <PVAStyleModal
                            notificationService={this.props.notificationService}
                            history={this.props.history}
                            location={this.props.location}
                            match={this.props.match}
                            allStyles={this.state.allStyles}
                            {...modalProps}
                        />
                    </PVAContainer>
                </div>;
            };

            var row = (
                <div key={i} className="style-block-row">
                    <div className="style-block-pair">
                        {
                            keys.slice(0, 2).map(renderPair)
                        }
                    </div>

                    <div className="style-block-pair">
                        {
                            keys.slice(2, 4).map(renderPair)
                        }
                    </div>
                </div>);

            resultingRows.push(row);
        }

        let defaultOptions: any[] = [{
            id: 0,
            value: '- Select -'
        }];
        
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllUsers) ) {
            defaultOptions.push({
                id: -100,
                value: 'All Users'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllCandidates) ) {
            defaultOptions.push({
                id: -200,
                value: 'All Candidates'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllFormerEmployees) ) {
            defaultOptions.push({
                id: -300,
                value: 'All Former Employees'
            });
        }

        var defaultUserOptions: any[] = [{
            id: -2,
            value: 'No Individuals'
        }, {
            id: -1,
            value: 'All Individuals'
        }];

        const getStylePercentagesForSelectedUsers = () => {
            const styleCounts:any[] = [];
            let total = 0;
            styleUserEntries.forEach((e:any) => {
                let count = styleUsers[e].users.length;
                count = count == null ? 0 : count;
                total += count;
                styleCounts.push({style:e,count:count,percent:0});
            });
            if (total === 0) {
                return styleCounts;
            }
            for(let x = 0; x < styleCounts.length; x++) {
                styleCounts[x].percent = Math.floor((styleCounts[x].count*1.0 / total)*100);
                styleCounts[x].total = total;
            }
            return styleCounts;
        };
        const getStylePercentagesPresentation = () => {
            let total = 0;
            let totalPercent = "0%";
            const styles = getStylePercentagesForSelectedUsers().map((p:any) => {
                total += p.count;
                return <>
                        <div key={"percent-breakdown-" + p.style} className="style-name">{p.style + "s: "}</div>
                        <div className="style-count">{p.count}</div>
                        <div className="style-percent">{p.percent + "%"}</div>
                </>;
            });
            if (total > 0) {
                totalPercent = "100%";
            }
            
            styles.push(<div className="separator-below"></div>);
            styles.push(<div className="separator-below"></div>);
            styles.push(<div className="separator-below"></div>);
            
            styles.push(
                <>
                    <div className="total-label bolded">
                        Total
                    </div>
                    <div className="total-count bolded">
                        {total}
                    </div>
                    <div className="total-percent bolded">
                        {totalPercent}
                    </div>
                </>
            );
            return styles;
        };

        var clarityContent = (
            <>
                <p>The Clarity Index highlights how consistently the individual completing the instrument responded to the statements on a number of scales compared to the maximum possible index that would be achievable by those scales by answering all the items associated with those scales.</p>
                <p>Therefore, the more elevated the index, it can be assumed that the individual is more clear about their preference for a particular style. Lower scores on the clarity index (shown in GREEN) only suggest that the individual is less clear about their preference for a particular style. The Clarity Index does not measure skills or ability or degree of use for a specific style, only clarity of preference.</p>
            </>
        );
        const exportPdf = async (e: any) => {
            this.setState({
                exporting: true
            });
            e.preventDefault();
            try {
                await this.reportDataService.getTeamologyPDF(this.state.surveyVersionId, this.state.teamId);
            }
            catch (e) {
                this.props.notificationService.addError(`Failed to export PDF. ${e.toString()}`);
            }
            finally {
                this.setState({
                    exporting: false
                });
            }
        }

        return (
            <PVAPage className={'component-PVATeamologyReport ' + (this.state.showClarity ? 'show-clarity' : '')}>
                {this.state.exporting && <LoadingIndicator showPdfDescription={true} showInPanel={true} showBackground={true} />}
                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            <span className="greyDark">PVA Teamology<span className="trademark">TM</span></span>
                            <span className="light"> for </span>
                            <PVATeamSelect defaultOptions={defaultOptions} options={this.teams} selected={this.teamId} onChange={(e: any) => { this.teamChange(e); }} disabled={this.state.miniLoad} />
                            {this.state.reportData && <PVAUserMultiSelect defaultOptions={defaultUserOptions} options={this.state.reportData.users} selected={this.state.selectedUserIds} onChange={(e: any) => this.toggleUserSelect(e)} FilterStore={this.props.FilterStore} disabled={this.state.miniLoad} />}
                            {this.state.miniLoad && <LoadingIndicatorMini />}
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                    <PVAPage.Header.Buttons>
                        <button className="btn btn-secondary" onClick={(e) => this.setState({ showClarity: !this.state.showClarity })}>
                            {this.state.showClarity ? <span>Hide Clarity</span> : <span>Show Clarity</span>}
                        </button>
                        <button className="btn btn-default" disabled={this.state.exporting || this.teamId <= 0} onClick={exportPdf}>
                            <img src="/images/Icon awesome-file-pdf.svg" className="pdf-icon" /> Export PDF
                        </button>
                    </PVAPage.Header.Buttons>
                </PVAPage.Header>

                <PVAPage.Body>
                    <div className="teamology-filters">
                        <div className="control-set">
                            <label>Cognitive Focus</label>
                            {(() => {
                                const cognitiveFocusOptions = [
                                    { id: 0, label: 'Primary Style' },
                                    { id: 1, label: 'Secondary Style' },
                                    ...(this.props.UserStore!.checkUserPermission(UserPermissions.TeamologyTertiaryAccess) ? [{ id: 2, label: 'Tertiary Style' }] : []),
                                    { id: 3, label: 'Least Preferred Style' }
                                ];
                                return <PVASelect options={cognitiveFocusOptions} sortOptions={(a,b) => a.id - b.id} selected={this.state.mode} onChange={(e: any) => { this.setState({ mode: Number(e.target.value) }); }} disabled={this.state.miniLoad} />;
                            })()}
                        </div>
                        <div className="control-set">
                            <label>Filter</label>
                            <input className="form-control text-input" type="text" placeholder="Filter Name" value={this.state.filter} onChange={(e) => this.setState({filter: e.target.value})} />
                        </div>
                    </div>
                    {this.state.loading && <LoadingIndicator />}
                    {!this.state.loading && <>
                        <div className="grid">
                            <div className="teamology-header-row">
                                <div className="teamology-header-indicator">
                                    {this.getConfigText(0)}
                                </div>
                                <div className="teamology-header-indicator">
                                    {this.getConfigText(1)}
                                </div>
                            </div>
                            
                            {resultingRows}
    
                            <div className="teamology-header-row">
                                <div className="teamology-header-indicator">
                                    {this.getConfigText(2)}
                                </div>
                                <div className="teamology-header-indicator">
                                    {this.getConfigText(3)}
                                </div>
                            </div>
                        </div>
                        
                        <PVAContainer id="breakdown-container" title="Breakdown" open={false}>
                            <div className="breakdown">
                                <div className="percentages">
                                    {getStylePercentagesPresentation()}
                                </div>
                                <div className="team-stats">
                                    {this.state.reportData && 
                                        <div className="total-line separator-below">
                                            <span>Average General Emotional Component:</span>
                                            <span className="line-percent">{Math.round(this.state.reportData.generalECAverage)}%</span>
                                        </div>
                                    }
                                    {this.state.reportData && 
                                        <div className="total-line separator-below">
                                            <span>Average Team Statisfaction:</span> 
                                            <span className="line-percent">{Math.round(this.state.reportData.satisfactionAverage)}%</span>
                                        </div>
                                    }
                                    <div className="total-line separator-below">
                                        <span>Total Users:</span> 
                                        <span className="line-percent">{userCount}</span>
                                    </div>
                                </div>
                                <div className="legend show-clarity">
                                    <div className="legend-label">
                                        <span className="icon-button">
                                            <OverlayTrigger animation={false} trigger={['hover', 'focus']} delay={0} placement="top" overlay={<Tooltip id="tooltip-clarity-index">{clarityContent}</Tooltip>}>
                                                <img src="/images/inf tooltip.svg" />
                                            </OverlayTrigger>
                                        </span>
                                        <span>Clarity Index</span>
                                    </div>
                                    <div className="clarity clarity-index clarity-low">Slight</div>
                                    <div className="clarity clarity-index clarity-mid">Moderate</div>
                                    <div className="clarity clarity-index clarity-high">Clear</div>
                                </div>
                            </div>
                        </PVAContainer>
                    </>}
                </PVAPage.Body>
                <PVAPage.Footer />
            </PVAPage>);
    }

    private getConfigText(ind: number) {
        if (!this.state.config) {
            return <span />;
        }
        switch (ind) {
            case 0:
                if (this.state.config && this.state.config.topLeft && this.state.config.topLeft !== '') {
                    return <span>{this.state.config.topLeft}</span>;
                }
                return <span>INFORMATION GATHERING: Adapting</span>;
            case 1:
                if (this.state.config && this.state.config.topRight && this.state.config.topRight !== '') {
                    return <span>{this.state.config.topRight}</span>;
                }
                return <span>DECISION-MAKING: Organizing</span>;
            case 2:
                if (this.state.config && this.state.config.bottomLeft && this.state.config.bottomLeft !== '') {
                    return <span>{this.state.config.bottomLeft}</span>;
                }
                return <span>INFORMATION GATHERING: Organizing</span>;
            case 3:
                if (this.state.config && this.state.config.bottomRight && this.state.config.bottomRight !== '') {
                    return <span>{this.state.config.bottomRight}</span>;
                }
                return <span>DECISION-MAKING: Adapting</span>;
            default:
                return <span />;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/teamology/TeamologyReport.tsx